import React, { useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Box,
  CardMedia,
  CardContent,
  Grid,
  IconButton,
  Divider,
} from "@mui/material";
import useLocalStorage from "../Components/Hooks/LocalStorage";
import { FETCH_CART } from "../graphql/queries";
import { Add, Remove } from "@mui/icons-material";
import { REMOVE_FROM_CART, UPDATE_CART_ITEMS } from "../graphql/mutations";
import PlaceholderPage from "./MyBiz/PlaceholderPage";

const CartPage: React.FC = () => {
  const cartId = useLocalStorage("shopifyCartId");
  const emptyImage1 = require("../assets/images/Empty1.png");


  const [fetchCart, { data, loading, error }] = useLazyQuery(FETCH_CART);
  const [removeFromCart] = useMutation(REMOVE_FROM_CART);
  const [updateCartItems] = useMutation(UPDATE_CART_ITEMS);

  const handleRemoveItem = async (lineId: String) => {
    const response = await removeFromCart({
          variables: {
            cartId,
            lineIds: [ lineId],
          },
          refetchQueries: [FETCH_CART]
        });
      console.log(response.data.removeFromCart)
  };

  const handleDecrementQuantity = async(lineId: String, newQuantity: number) => {
    if (newQuantity === 0) {
      handleRemoveItem(lineId);
      return;
    }

    const response = await updateCartItems({
      variables: {
        cartId,
        items: {
          id: lineId,
          quantity: newQuantity,
        }
      },
      refetchQueries: [FETCH_CART],
    });

    console.log(response.data.updateCartItems);
  }

  const handleIncrementQuantity = async(lineId: String, newQuantity: number) => {
    const response = await updateCartItems({
      variables: {
        cartId,
        items: {
          id: lineId,
          quantity: newQuantity,
        }
      },
      refetchQueries: [FETCH_CART],
    });

    console.log(response.data.updateCartItems);
  }


  useEffect(() => {
    if (cartId) {
      fetchCart({ variables: { cartId } });
    }
  }, [cartId, fetchCart]);

  const handleCheckout = async () => {
    if (!cartId) {
      alert("No cart found!");
      return;
    }

    try {
      const checkoutUrl = data.fetchCart.checkoutUrl;
      localStorage.removeItem("shopifyCartId")
      window.location.href = checkoutUrl; // Redirect to checkout
    } catch (error: any) {
      console.error("Error during checkout:", error.message);
      alert("Checkout failed. Please try again.");
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <p>Error fetching cart: {error.message}</p>;

  const cart = data?.fetchCart;

  if (!cartId || !cart || !cart.lines || cart.lines.length === 0) {
    return (
       <PlaceholderPage
          title="Cart"
          emptyStateText="Your cart is empty."
          emptyStateImage={emptyImage1}
          actionButtons={<></>}
        />
    )
  }

  return (
    <Container maxWidth="xl" id='CartContainer' sx={{backgroundColor: '#f5f5f5', paddingTop: 2, paddingBottom: 2, borderRadius: 16 }} style={{borderRadius: 16, justifyContent: "center", justifyItems: "center"}}>
      <Typography variant="h4" gutterBottom mt={2}>
        Your Cart
      </Typography>
      {cart.lines.map((line: any) => (
        <>

        <Grid container direction={"row"} spacing={1} maxWidth={'md'} alignContent={"center"} alignItems={"center"}>
          <Grid item xs={2}>
            <CardMedia
              component="img"
              sx={{ width: 150 }}
              image={line.merchandise.image?.src || ""}
              alt={line.merchandise.image?.altText || "Product image"}
            />
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
              <CardContent>
                <Box alignContent={"start"} alignItems={"start"} display={"flex"} flexDirection={"column"}>
                  <Typography variant="subtitle1" textAlign={"left"}>
                    {line.merchandise.product.title}
                  </Typography>
                  {line.merchandise.selectedOptions.map((option: {name: string, value: string}) => 
                      (<Typography variant="overline">
                        {option.name}: {option.value}
                      </Typography>)
                  )}
                  <Button variant="outlined" color="error" sx={{mt:1}} size="small" onClick={() => handleRemoveItem(line.id)}>
                    Remove
                  </Button>
                </Box>
              </CardContent>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box flexDirection={"row"} display={"flex"} alignContent={"center"} alignItems={"center"}>
              <IconButton aria-label="remove" color="secondary" onClick={() => handleDecrementQuantity(line.id, line.quantity-1)}>
                <Remove/>
              </IconButton>
              <Typography variant="h6" px={1}>
                {line.quantity}
              </Typography>

              <IconButton aria-label="add" color="secondary" onClick={() => handleIncrementQuantity(line.id, line.quantity+1)}>
                <Add/>
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body1">
              $ {line.merchandise.price.toFixed(2)}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{marginTop: "1px", borderWidth: "1px"}}/>
        </>
      ))}
      <Typography variant="h6" sx={{ marginTop: 3 }}>
        Total Price:{" $"}
        {cart.lines
          .reduce(
            (sum: number, line: any) =>
              sum + line.quantity * line.merchandise.price,
            0
          )
          .toFixed(2)}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={handleCheckout}
        sx={{ marginTop: 2 }}
      >
        {loading ? "Processing..." : "Proceed to Checkout"}
      </Button>
    </Container>
  );
};

export default CartPage;
